// Supply Network panel: confirm modal that appears before a bulk
// dealer-location plant reassignment when ANY selected location already
// has a primary_plant_id (different from the target). Per the slice
// spec, the friction is intentional but mild — reassignment is a
// legitimate change as orgs grow, so we just surface the from→to list
// rather than gating behind a higher-security prompt.
//
// Mirrors `ManufacturingMaterialDeleteConfirm`'s shape (modal scrim,
// centered card, ghost cancel + primary confirm) so the manufacturing
// suite continues to feel coherent without dragging in a generic
// confirm primitive yet.
import type { PendingReassignment } from "./useManufacturingPlantDealerLocations";
import { ghostBtnClass, mfgConfirmDialogClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

type Props = {
  open: boolean;
  reassignments: PendingReassignment[];
  netNewAssignmentCount: number;
  targetPlantLabel: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const warnBtnClass =
  "rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60";

export function ManufacturingPlantDealerLocationsReassignConfirm({
  open,
  reassignments,
  netNewAssignmentCount,
  targetPlantLabel,
  busy = false,
  onCancel,
  onConfirm,
}: Props) {
  if (!open) return null;
  const reassignCount = reassignments.length;
  const totalChanges = reassignCount + netNewAssignmentCount;
  const summary =
    netNewAssignmentCount > 0
      ? `Reassign ${reassignCount} dealer location${reassignCount === 1 ? "" : "s"} (and assign ${netNewAssignmentCount} previously unassigned)`
      : `Reassign ${reassignCount} dealer location${reassignCount === 1 ? "" : "s"}`;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mfg-dealer-loc-reassign-title"
      onClick={busy ? undefined : onCancel}
    >
      <div
        className={`max-w-lg ${mfgConfirmDialogClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="mfg-dealer-loc-reassign-title"
          className="text-base font-semibold text-gray-900 dark:text-zinc-100"
        >
          Reassign to {targetPlantLabel}?
        </h3>
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          {summary}. This is reversible — re-running the bulk assign will
          point them back wherever you choose.
        </p>
        <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-200 dark:border-zinc-700">
          <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
            {reassignments.map((r) => (
              <li
                key={r.id}
                className="flex flex-wrap items-baseline gap-x-2 px-3 py-2 text-xs text-gray-700 dark:text-zinc-200"
              >
                <span className="font-medium text-gray-900 dark:text-zinc-100">
                  {r.name}
                </span>
                <span className="text-gray-500 dark:text-zinc-400">
                  {r.fromPlantLabel} → {r.toPlantLabel}
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            className={ghostBtnClass}
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={warnBtnClass}
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {busy
              ? "Applying…"
              : `Confirm ${totalChanges} change${totalChanges === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
